import { Card, Col, Row, Typography, theme as antdTheme } from 'antd';
import {
  ApartmentOutlined,
  CalendarOutlined,
  DollarOutlined,
  IdcardOutlined,
  RightOutlined,
  ScheduleOutlined,
  StarOutlined,
  TeamOutlined,
  TrophyOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { PageHeader } from '@/shared/components/page-header';

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const tiles: Tile[] = [
  {
    to: '/hr/staff',
    title: 'Staff',
    description: 'Profiles, employment, contact, bank, qualifications, and experience.',
    icon: <TeamOutlined />,
  },
  {
    to: '/hr/designations',
    title: 'Designations',
    description: 'Job titles like Teacher, Accountant, Vice-Principal — optionally grouped by department.',
    icon: <IdcardOutlined />,
  },
  {
    to: '/hr/attendance',
    title: 'Staff attendance',
    description: 'Daily grid + monthly summary with working-days % per staff member.',
    icon: <CalendarOutlined />,
  },
  {
    to: '/hr/leave',
    title: 'Leave management',
    description: 'Types, quotas, requests with approval workflow, and per-staff balances.',
    icon: <CalendarOutlined />,
  },
  {
    to: '/hr/salary-components',
    title: 'Salary components',
    description: 'Earnings (Basic, HRA) and deductions (tax, EOBI). Used by salary structures.',
    icon: <WalletOutlined />,
  },
  {
    to: '/hr/salary-structure',
    title: 'Salary structures',
    description: 'Per-staff salary breakdown with revision history and effective dates.',
    icon: <ScheduleOutlined />,
  },
  {
    to: '/hr/salary-advances',
    title: 'Salary advances',
    description: 'Disbursement and EMI recovery in payroll.',
    icon: <DollarOutlined />,
  },
  {
    to: '/hr/payroll',
    title: 'Payroll',
    description: 'Monthly draft → finalize, slip numbers, advance recovery.',
    icon: <DollarOutlined />,
  },
  {
    to: '/hr/evaluation-criteria',
    title: 'Evaluation criteria',
    description: 'Aspects staff are evaluated on (numeric or label rating). Configure once per school.',
    icon: <StarOutlined />,
  },
  {
    to: '/hr/evaluations',
    title: 'Performance evaluations',
    description: 'Per-staff annual or per-term evaluation against the configured criteria.',
    icon: <TrophyOutlined />,
  },
];

export function HrHubPage() {
  const { token } = antdTheme.useToken();
  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageHeader
        eyebrow="Module"
        title="Staff & HR"
        description="Staff profiles, leave management, salary structures, advances, and monthly payroll."
      />

      <Row gutter={[16, 16]}>
        {tiles.map((t) => (
          <Col key={t.to} xs={24} md={12} xl={8}>
            <Link to={t.to} style={{ textDecoration: 'none' }}>
              <Card
                hoverable
                className="lift-card"
                style={{ height: '100%', borderColor: token.colorBorderSecondary }}
                styles={{ body: { padding: 20 } }}
              >
                <div style={{ display: 'flex', gap: 14 }}>
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: token.colorFillTertiary,
                      color: token.colorPrimary,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {t.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <Typography.Text strong style={{ fontSize: 15 }}>
                        {t.title}
                      </Typography.Text>
                      <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {t.description}
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      <Card>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <ApartmentOutlined /> The simple lookups for <b>departments</b> and <b>employment types</b> are managed under <Link to="/setup/lookups">Setup → Lookup catalogs</Link>. Leave types live inside <Link to="/hr/leave?tab=types">Leave management</Link>.
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
